// src/hooks/useSessionGuard.js
// Centralized session validation + cross-tab auth sync + deployment update detection.
//
// Responsibilities:
//  1. Validate session before any protected action (payment, download)
//  2. Sync logout across all open tabs via BroadcastChannel
//  3. Detect new deployments and show update banner
//  4. Heartbeat: re-validate session every 5 min while app is open
//  5. Stale-chunk recovery: reload if SW signals a missing chunk

import { useState, useEffect, useCallback, useRef } from 'react';
import { readSession, clearSession } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { invalidatePremiumCache } from './useInsightLibrary';

const AUTH_CHANNEL    = 'tv-auth-sync';
const UPDATE_CHANNEL  = 'tv-sw-updates';
const HEARTBEAT_MS    = 5 * 60 * 1000; // 5 minutes

// ── Validate session locally (no network) ────────────────────────────────────
// Returns { valid: bool, reason: string }
export function validateSessionLocally() {
  const session = readSession();
  if (!session)              return { valid: false, reason: 'no_session' };
  if (!session.token)        return { valid: false, reason: 'no_token' };
  if (!session.email)        return { valid: false, reason: 'no_email' };
  if (!session.verified)     return { valid: false, reason: 'not_verified' };
  if (session.expiresAt < Date.now()) return { valid: false, reason: 'expired' };
  return { valid: true, reason: 'ok' };
}

// ── Main hook ─────────────────────────────────────────────────────────────────
export function useSessionGuard() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion,      setNewVersion]      = useState(null);
  const heartbeatRef = useRef(null);
  const authChannelRef   = useRef(null);
  const updateChannelRef = useRef(null);

  // ── Guard: call before opening payment modal or triggering download ─────────
  // Returns true if session is valid.
  // If invalid: clears session, shows toast, returns false.
  // Caller should NOT open the modal if this returns false.
  const guardSession = useCallback((opts = {}) => {
    const { redirectOnFail = false, toastMessage } = opts;
    const { valid, reason } = validateSessionLocally();

    if (valid) return true;

    // Clear stale session data
    clearSession();

    const msg = toastMessage ||
      (reason === 'expired'
        ? 'Your session expired. Please sign in again.'
        : 'Please sign in to continue.');

    toast.error(msg, { duration: 4000, position: 'top-center' });

    if (redirectOnFail) {
      // Navigate to home — safe for both browser and PWA standalone mode
      setTimeout(() => { window.location.href = '/'; }, 300);
    }

    return false;
  }, []);

  // ── BroadcastChannel: auth sync across tabs ─────────────────────────────────
  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;

    // Auth channel: receive logout/session-revoked from other tabs
    try {
      authChannelRef.current = new BroadcastChannel(AUTH_CHANNEL);
      authChannelRef.current.onmessage = (e) => {
        if (e.data?.type === 'LOGOUT') {
          clearSession();
          const isProtectedRoute = window.location.pathname.includes('submit-event') || 
                                   window.location.pathname.includes('resume-builder') ||
                                   window.location.pathname.includes('insight-library');
          if (isProtectedRoute) {
            window.location.href = '/';
          }
        }
        if (e.data?.type === 'SESSION_REVOKED') {
          clearSession();
          toast.error('Your session was ended on another device.', { duration: 5000 });
          setTimeout(() => { window.location.href = '/'; }, 1500);
        }
        if (e.data?.type === 'PREMIUM_ACTIVATED') {
          const email = e.data.email;
          invalidatePremiumCache(email);
          window.dispatchEvent(new CustomEvent('premium-activated', { detail: { email } }));
        }
      };
    } catch (_) {}

    // Update channel: receive new deployment notification from SW
    try {
      updateChannelRef.current = new BroadcastChannel(UPDATE_CHANNEL);
      updateChannelRef.current.onmessage = (e) => {
        if (e.data?.type === 'NEW_VERSION') {
          setUpdateAvailable(true);
          setNewVersion(e.data.version || null);
        }
      };
    } catch (_) {}

    return () => {
      try { authChannelRef.current?.close(); }   catch (_) {}
      try { updateChannelRef.current?.close(); } catch (_) {}
    };
  }, []);

  // ── Stale chunk recovery: Check immediately on mount ────────────────────────
  useEffect(() => {
    if (window.__SW_CHUNK_MISSING__) {
      console.warn('[App] Stale chunk detected on mount — reloading for fresh assets');
      // Clear flag to avoid infinite reload loop
      try { delete window.__SW_CHUNK_MISSING__; } catch (e) { window.__SW_CHUNK_MISSING__ = undefined; }
      window.location.reload(true);
    }
  }, []);

  // ── SW message listener: CACHE_UPDATED + stale chunk recovery ───────────────
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onSWMessage = (e) => {
      if (e.data?.type === 'CACHE_UPDATED') {
        setUpdateAvailable(true);
        setNewVersion(e.data.version || null);
      }
      // Stale chunk recovery: SW signals a missing JS chunk
      if (e.data?.type === 'CHUNK_MISSING' || window.__SW_CHUNK_MISSING__) {
        console.warn('[App] Stale chunk detected — reloading for fresh assets');
        window.location.reload(true);
      }
    };

    navigator.serviceWorker.addEventListener('message', onSWMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onSWMessage);
  }, []);

  // ── Heartbeat: re-validate session every 5 min ──────────────────────────────
  useEffect(() => {
    heartbeatRef.current = setInterval(() => {
      const { valid, reason } = validateSessionLocally();
      if (!valid && reason !== 'no_session') {
        // Session was valid before but expired — clear, notify, and broadcast
        clearSession();
        toast.error('Your session expired. Please sign in again.', {
          duration: 5000,
          position: 'top-center',
        });
        // Broadcast to all other tabs so they clear state simultaneously
        try {
          const bc = new BroadcastChannel(AUTH_CHANNEL);
          bc.postMessage({ type: 'SESSION_REVOKED' });
          bc.close();
        } catch (_) {}
      }
    }, HEARTBEAT_MS);

    return () => clearInterval(heartbeatRef.current);
  }, []);

  // ── Tab focus: re-validate on return ────────────────────────────────────────
  useEffect(() => {
    const onFocus = () => {
      const { valid, reason } = validateSessionLocally();
      if (!valid && reason !== 'no_session') {
        clearSession();
      }
    };
    // Store handler reference so it can be properly removed
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // ── Apply update: reload safely preserving valid session ────────────────────
  const applyUpdate = useCallback(() => {
    // Only reload if session is valid — don't interrupt a payment in progress
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    // Small delay so SW can activate before reload
    setTimeout(() => window.location.reload(true), 300);
  }, []);

  // ── Broadcast logout to all tabs ─────────────────────────────────────────────
  const broadcastLogout = useCallback(() => {
    try {
      const bc = new BroadcastChannel(AUTH_CHANNEL);
      bc.postMessage({ type: 'LOGOUT' });
      bc.close();
    } catch (_) {}
    // Also tell SW to clear dynamic cache
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'LOGOUT' });
    }
  }, []);

  return {
    guardSession,
    updateAvailable,
    newVersion,
    applyUpdate,
    broadcastLogout,
  };
}

export default useSessionGuard;
